import React, { useState } from 'react'
import { ArrowPathIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline'

import { DeploymentJob, DeploymentJobItem } from '../services/api'
import { dateTime } from '../utils/date'
import { plainJobError } from '../utils/jobError'

/**
 * What each deployment run actually published, item by item.
 *
 * A job's own status answers "did it finish running", which is not the question an operator has. It
 * can finish having published four of five applications, and until now that read as `completed` with
 * nothing naming the fifth. Every fact needed to see it was already recorded per item and none of it
 * was rendered anywhere.
 *
 * TWO KINDS OF UNHAPPY, KEPT APART, because they call for opposite actions:
 *
 *   failed          nothing was created, so a retry is safe and is offered.
 *   needs attention whether the application exists is UNKNOWN — the worker died mid-publish, or the
 *                   publish returned no id. A retry is the move that creates a duplicate, so the
 *                   item must be checked in Intune first. Retry is withheld while any exist, and
 *                   they are named.
 *
 * Collapsing that distinction into one "problem" count is what makes an operator retry a job they
 * should have inspected, and two identical applications in a tenant cannot be told apart afterwards.
 */

const ITEM_STATE: Record<DeploymentJobItem['status'], { label: string; tone: string; hint: string }> = {
  pending: { label: 'Pending', tone: 'border-theme-border-primary text-theme-text-tertiary',
             hint: 'Not started yet.' },
  processing: { label: 'Publishing', tone: 'border-blue-400/40 text-blue-400',
                hint: 'Publishing to Intune now.' },
  succeeded: { label: 'Published', tone: 'border-emerald-400/40 text-emerald-400',
               hint: 'The application exists in Intune. A retry skips it rather than creating a second.' },
  failed: { label: 'Failed', tone: 'border-red-400/40 text-red-400',
            hint: 'Nothing was created, so retrying this is safe.' },
  skipped: { label: 'Not attempted', tone: 'border-amber-400/40 text-amber-400',
             hint: 'Its branding did not publish, so this was never attempted. Retrying runs it.' },
  needs_attention: { label: 'Needs checking', tone: 'border-amber-500/50 text-amber-500',
                     hint: 'Whether this application exists in Intune is unknown. Check there before retrying — retrying is what would create a duplicate.' },
}

const JOB_STATE: Record<DeploymentJob['status'], { label: string; tone: string }> = {
  queued: { label: 'Queued', tone: 'border-theme-border-primary text-theme-text-tertiary' },
  processing: { label: 'Running', tone: 'border-blue-400/40 text-blue-400' },
  completed: { label: 'Completed', tone: 'border-emerald-400/40 text-emerald-400' },
  failed: { label: 'Failed', tone: 'border-red-400/40 text-red-400' },
  cancelled: { label: 'Not needed', tone: 'border-theme-border-primary text-theme-text-tertiary' },
}

const Pill: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${className ?? ''}`}>
    {children}
  </span>
)

// The backend sends naive UTC timestamps; parsed as they are they would read as local time.
const when = (iso?: string | null) => (iso ? dateTime(iso) : null)

/** What this run is deploying, by name.
 *
 *  The applications only — a branding item is named after the branding, and leading with that would
 *  say a run deployed something the reader did not ask for. Falls back to the reference when an
 *  item carries no name, and to the count when there are no application items at all. */
export function jobTitle(job: DeploymentJob): string {
  const apps = job.items.filter((i) => i.type === 'app')
  const named = apps.map((i) => i.name || i.ref_id).filter(Boolean)
  if (job.automatic) {
    const a = job.automatic
    const which = a.deployment_name ? ` (${a.deployment_name})` : ''
    return `Automatic update · ${named[0] ?? 'application'}${which} ${a.from_version ?? '?'} → ${a.to_version ?? '?'}`
  }
  if (!named.length) return `${job.total} item${job.total === 1 ? '' : 's'}`
  const shown = named.slice(0, 2).join(', ')
  return named.length > 2 ? `${shown} +${named.length - 2} more` : shown
}

/** What this run amounts to, in a sentence. `completed` alone is read as "everything published",
 *  which is exactly the misreading a partial failure needs not to survive. */
export function jobSummaryLine(job: DeploymentJob): string {
  const parts = [`${job.succeeded} of ${job.total} published`]
  if (job.failed) parts.push(`${job.failed} failed`)
  if (job.needs_attention) parts.push(`${job.needs_attention} needs checking`)
  return parts.join(' · ')
}

const DeploymentJobsPanel: React.FC<{
  jobs: DeploymentJob[]
  loading?: boolean
  error?: string | null
  /** Absent while a retry is in flight, so one cannot be started twice. */
  onRetry?: (job: DeploymentJob) => void
  retryingId?: string | null
  /** Whether a run starts open, when the caller knows better than "something went wrong" does —
   *  a run still publishing is worth watching even though nothing has failed. */
  expandedByDefault?: (job: DeploymentJob) => boolean
  /** Offers a dismiss control. Absent means the run cannot be dismissed. */
  onDismiss?: (job: DeploymentJob) => void
}> = ({ jobs, loading, error, onRetry, retryingId, expandedByDefault, onDismiss }) => {
  const [open, setOpen] = useState<Record<string, boolean>>({})

  if (loading) return <p className="text-sm text-theme-text-tertiary">Loading deployment runs…</p>
  if (error) return <p className="text-sm text-red-400">{error}</p>
  if (!jobs.length) {
    return (
      <div className="rounded-lg border border-dashed border-theme-border-primary px-4 py-6">
        <p className="text-sm text-theme-text-secondary">No deployment runs yet.</p>
        <p className="mt-1 text-xs text-theme-text-tertiary">
          Deploying applications records a run here, with what each one published.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {jobs.map((job) => {
        const state = JOB_STATE[job.status] ?? JOB_STATE.queued
        // Retry is offered only where it is SAFE and USEFUL: the run has stopped, something did
        // not get through, and nothing is in the unknown state that a retry would duplicate. A
        // retried run is queued again with its failed items still counted as failed until the
        // worker reaches them, so a second Retry then would publish them twice.
        const blocked = job.needs_attention > 0
        const running = job.status === 'queued' || job.status === 'processing'
        const worthRetrying =
          !running && (job.failed > 0 || job.items.some((i) => i.status === 'skipped'))
        const expanded = open[job.id]
          ?? (expandedByDefault ? expandedByDefault(job) : job.failed > 0 || job.needs_attention > 0)

        return (
          <div key={job.id} className="rounded-lg border border-theme-border-primary bg-theme-primary">
            <div className="flex items-start justify-between gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setOpen((p) => ({ ...p, [job.id]: !expanded }))}
                    aria-expanded={expanded}
                    className="flex items-center gap-1 text-sm font-medium text-theme-text-primary"
                  >
                    <ChevronRightIcon
                      className={`h-4 w-4 transition-transform ${expanded ? 'rotate-90' : ''}`}
                    />
                    <span className="truncate">{jobTitle(job)}</span>
                  </button>
                  <Pill className={state.tone}>{state.label}</Pill>
                </div>

                {/* The counts, always — a job that finished having published four of five reads as
                    'Completed' otherwise, and the fifth is never looked for. */}
                <p className="mt-1 text-xs text-theme-text-secondary">{jobSummaryLine(job)}</p>

                {job.error && <p className="mt-1 text-xs text-red-400">{plainJobError(job.error)}</p>}

                {blocked && (
                  <p className="mt-1.5 rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-3 py-2 text-xs text-amber-500">
                    Some items could not be confirmed. Whether their applications exist in Intune is
                    unknown, so retrying could create a second copy that nothing can tell from the
                    first. Check them there, then retry.
                  </p>
                )}

                {when(job.finished_at) && (
                  <p className="mt-1 text-[11px] text-theme-text-tertiary">
                    Finished {when(job.finished_at)}
                  </p>
                )}
              </div>

              {onRetry && worthRetrying && (
                <button
                  type="button"
                  onClick={() => !blocked && onRetry(job)}
                  disabled={blocked || retryingId === job.id}
                  title={
                    blocked
                      ? 'Items whose outcome is unknown must be checked in Intune first — retrying now could create a duplicate application.'
                      : 'Publish what did not get through. Items that already published are skipped.'
                  }
                  className={`shrink-0 inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs ${
                    blocked || retryingId === job.id
                      ? 'cursor-not-allowed border-theme-border-primary/50 text-theme-text-tertiary opacity-60'
                      : 'border-theme-border-primary text-theme-text-primary hover:bg-theme-tertiary'
                  }`}
                >
                  <ArrowPathIcon className={`h-3.5 w-3.5 ${retryingId === job.id ? 'animate-spin' : ''}`} />
                  {retryingId === job.id ? 'Retrying…' : 'Retry'}
                </button>
              )}

              {onDismiss && (
                <button
                  type="button"
                  onClick={() => onDismiss(job)}
                  aria-label="Dismiss this deployment run"
                  title="Dismiss. The next deployment shows here in its place."
                  className="shrink-0 rounded-lg p-1 text-theme-text-tertiary hover:bg-theme-tertiary hover:text-theme-text-primary"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
            </div>

            {expanded && (
              <div className="border-t border-theme-border-primary px-4 py-2">
                {job.items.map((item) => {
                  const s = ITEM_STATE[item.status] ?? ITEM_STATE.pending
                  return (
                    <div key={item.id} className="flex items-start justify-between gap-3 py-1.5">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="truncate text-xs text-theme-text-primary">
                            {item.name || item.ref_id}
                          </span>
                          {item.type === 'branding' && <Pill className="border-theme-border-primary text-theme-text-tertiary">Branding</Pill>}
                        </div>
                        {item.error && <p className="mt-0.5 text-[11px] text-red-400">{plainJobError(item.error)}</p>}
                        <p className="mt-0.5 text-[11px] text-theme-text-tertiary">
                          {item.outcome?.mode === 'updated_in_place'
                            ? 'Updated in place: the deployed application got the chosen templates; no second application was created.'
                            : s.hint}
                        </p>
                        {/* What the publish could not do. A warning nobody sees is not a warning:
                            these used to stop at the worker. */}
                        {(item.outcome?.warnings ?? []).map((w) => (
                          <p key={w} className="mt-0.5 text-[11px] text-amber-500">{w}</p>
                        ))}
                        {/* Shown because "did this actually publish" is answered by whether an
                            application id was recorded, and that is the same fact a retry keys off. */}
                        {item.intune_app_id && (
                          <p className="mt-0.5 font-mono text-[11px] text-theme-text-tertiary">
                            {item.intune_app_id}
                          </p>
                        )}
                        {item.attempts > 1 && (
                          <p className="mt-0.5 text-[11px] text-theme-text-tertiary">
                            {item.attempts} attempts
                          </p>
                        )}
                      </div>
                      <Pill className={`shrink-0 ${s.tone}`}>
                        {item.outcome?.mode === 'updated_in_place' && item.status === 'succeeded'
                          ? 'Updated in place'
                          : s.label}
                      </Pill>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default DeploymentJobsPanel
